import * as React from 'react'
import moment from 'moment'
import { inject, observer } from 'mobx-react'
import { getCubeAPI } from '../../../../../../../Cube'
import { QueryRenderer } from '@cubejs-client/react'
import { Skeleton } from 'antd'
import { UncontrolledButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap'
import { parseAndDownloadCSV, GrPrice } from '../../../../../../../utilities/helpers'
import { ReportFilter } from '../../../ReportFilter'
import { ReportFilterStore } from '../../../ReportFilterStore'
import Helmet from 'react-helmet'
import { Track } from '../../../../../../../Track'
import { browserHistory } from 'react-router'
import { InsightsRoutes, Routes } from '../../../../../../../constants/Routes'
import { ReportAlert } from '../../../ReportAlert'
import { PaymentTypeIds, convertPaymentTypeIdToName } from '@getgreenline/homi-shared'

interface Props {
  companyId: number
  reportFilterStore?: ReportFilterStore
}

type timeGroup = 'hour' | 'day' | 'week' | 'month'

interface State {
  groupBy: timeGroup
}

const query = {
  measures: ['Transactions.count', 'Transactions.amount'],
  timeDimensions: [],
  dimensions: ['Locations.name', 'PaymentTypes.name'],
  filters: [
    {
      dimension: 'Payments.status',
      operator: 'equals',
      values: [`completed`],
    },
    {
      member: 'PaymentTypes.name',
      operator: 'set',
    },
  ],
  order: {
    'Payments.completedate': 'asc',
    'Locations.name': 'asc',
    'PaymentTypes.name': 'asc',
  },
  limit: 10000,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
}

function converRawDataToTableData(rawData: any) {
  // Looks like the following:
  // [
  //   {
  //     Locations.name: "The Pot Shack"
  //     PaymentTypes.name: "cash"
  //     Payments.completedate: "2019-03-01T00:00:00.000"
  //     Transactions.amount: 6688.45
  //   }
  // ]
  const allAvailablePaymentTypes = [
    ...new Set<string>(rawData.map((r: any) => r['PaymentTypes.name'])),
  ]

  const tableData: any[] = []

  for (const data of rawData) {
    const date = data['Payments.completedate']
    const location = data['Locations.name']
    const paymentType = data['PaymentTypes.name']
    const amount = data['Transactions.amount']

    const lastTableDataEntry = tableData.length > 0 ? tableData[tableData.length - 1] : null
    if (
      lastTableDataEntry &&
      lastTableDataEntry['Payments.completedate'] === date &&
      lastTableDataEntry['Locations.name'] === location
    ) {
      lastTableDataEntry[paymentType] = amount
    } else {
      const dataEntry: any = {
        'Locations.name': data['Locations.name'],
        'Payments.completedate': data['Payments.completedate'],
      }
      // Insert payment types into entries, defaulted to 0
      for (const paymentType of allAvailablePaymentTypes) {
        dataEntry[paymentType] = 0
      }
      dataEntry[paymentType] = amount
      tableData.push(dataEntry)
    }
  }

  return tableData
}

@inject('reportFilterStore')
@observer
export class PaymentTypesReport extends React.Component<Props, State> {
  // Static property that stores loaded report data
  data: any

  constructor(props: Props) {
    super(props)
    this.state = {
      groupBy: 'day',
    }
  }

  getQuery = () => {
    const { startDateString, endDateString, selectedLocationIds } = this.props.reportFilterStore!

    const fullQuery = {
      ...query,
      timeDimensions: [
        {
          dimension: 'Payments.completedate',
          granularity: this.state.groupBy,
          dateRange: [startDateString, endDateString],
        },
      ],
      filters: [
        ...query.filters,
        {
          dimension: 'Locations.id',
          operator: 'equals',
          values: selectedLocationIds.map((id) => `${id}`),
        },
      ],
    }

    return fullQuery
  }

  tableRenderer = ({ resultSet }: any) => {
    const rawData: any[] = resultSet.rawData()
    const tableData = converRawDataToTableData(rawData)
    const allAvailablePaymentTypes = [
      ...new Set<string>(rawData.map((r: any) => r['PaymentTypes.name'])),
    ]

    if (rawData.length > 0) {
      return (
        <table className='table table-sm'>
          <thead>
            <tr>
              <th>Location</th>
              <th>Date</th>
              {allAvailablePaymentTypes.map((paymentType) => {
                const paymentName =
                  convertPaymentTypeIdToName(PaymentTypeIds[paymentType]) || paymentType
                return (
                  <th key={paymentType} className='text-right'>
                    {paymentName}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {tableData.map((data, index) => {
              return (
                <tr key={index}>
                  <td>{data['Locations.name']}</td>
                  <td>{moment(data['Payments.completedate']).format('YYYY-MM-DD')}</td>
                  {allAvailablePaymentTypes.map((paymentType) => {
                    return (
                      <td key={paymentType} className='text-right'>
                        {GrPrice.convertDollarToDollar(data[paymentType])}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      )
    } else {
      return <div>No data</div>
    }
  }

  renderChart(Component: any) {
    const { startDate, endDate } = this.props.reportFilterStore!
    // eslint-disable-next-line react/prop-types
    return ({ resultSet, error }: any) => {
      if (resultSet) {
        Track.track('dashboard:reports_paymentTypes', {
          numberOfDays: moment(endDate).diff(startDate, 'days'),
        })
        // eslint-disable-next-line react/prop-types
        this.data = resultSet.rawData()
      } else {
        Track.startTimer('dashboard:reports_paymentTypes')
        this.data = undefined
      }
      return (
        (resultSet && <Component resultSet={resultSet} />) ||
        (error && error.toString()) || <Skeleton active />
      )
    }
  }

  exportCSV() {
    const { startDate, endDate } = this.props.reportFilterStore!
    const data = converRawDataToTableData(this.data)
    if (data) {
      parseAndDownloadCSV(`payment-types-${startDate}-${endDate}`, data)
    }
  }

  toReports = () => {
    const { reportFilterStore } = this.props
    if (!reportFilterStore) {
      return
    }

    reportFilterStore.setIsFirstLoad(true)
    browserHistory.push({
      pathname: this.inSightReportPath,
      query: {
        startDate: reportFilterStore.startDateString,
        endDate: reportFilterStore.endDateString,
        locationIds: reportFilterStore.selectedLocationIds,
      },
    })
  }

  get inSightReportPath(): string {
    return Routes.INSIGHTS_REPORTS(this.props.companyId, InsightsRoutes.PAYMENT_TYPES)
  }

  render() {
    const { companyId } = this.props
    const fullQuery = this.getQuery()

    return (
      <>
        <Helmet>
          <title>Reports | Payment Types</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <ReportFilter reportPath={this.inSightReportPath} />
        <ReportAlert reportType='insights' reportPath={this.inSightReportPath} />

        <div className='rounded background-white'>
          <div className='p-3'>
            <div>
              <b>Payment types</b>
            </div>
            <div className='text-muted'>Over time</div>

            <div className='mt-2 mb-2'>
              <UncontrolledButtonDropdown className='mr-2'>
                <DropdownToggle caret size='sm' color='light' style={{ textAlign: 'left' }}>
                  Group by {this.state.groupBy}
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem onClick={() => this.setState({ groupBy: 'day' })}>day</DropdownItem>
                  <DropdownItem onClick={() => this.setState({ groupBy: 'week' })}>
                    week
                  </DropdownItem>
                  <DropdownItem onClick={() => this.setState({ groupBy: 'month' })}>
                    month
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledButtonDropdown>
              <button className='btn btn-light btn-sm' onClick={() => this.exportCSV()}>
                Export as csv
              </button>
            </div>

            <div className='table-responsive'>
              {fullQuery ? (
                <QueryRenderer
                  query={fullQuery}
                  cubejsApi={getCubeAPI(companyId)}
                  render={this.renderChart(this.tableRenderer)}
                />
              ) : (
                <Skeleton active />
              )}
            </div>
          </div>
        </div>
      </>
    )
  }
}
